/**
 * Critical Resource Loading Optimization
 * Implements resource hints, connection-aware loading, and critical CSS extraction
 */

export interface ResourceHint {
  rel: 'preload' | 'prefetch' | 'preconnect' | 'dns-prefetch' | 'modulepreload'
  href: string
  as?: 'script' | 'style' | 'font' | 'image' | 'document' | 'fetch'
  type?: string
  crossOrigin?: 'anonymous' | 'use-credentials'
  media?: string
  priority?: 'high' | 'low'
  condition?: () => boolean
}

export interface CriticalResource {
  type: 'css' | 'js' | 'font' | 'image'
  url: string
  priority: 'critical' | 'high' | 'medium' | 'low'
  loadStrategy: 'preload' | 'prefetch' | 'lazy' | 'defer'
  condition?: 'above-fold' | 'below-fold' | 'interaction' | 'connection-aware'
}

/**
 * Connection-aware loading strategies
 */
export class ConnectionAwareLoader {
  private connectionType: 'slow-2g' | '2g' | '3g' | '4g' | 'wifi' | 'unknown' = 'unknown'
  private saveData: boolean = false

  constructor() {
    this.detectConnection()
  }

  private detectConnection(): void {
    if (typeof navigator !== 'undefined') {
      // @ts-expect-error - navigator.connection is experimental
      const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection
      
      if (connection) {
        this.connectionType = connection.effectiveType || 'unknown'
        this.saveData = connection.saveData || false
      }

      // Listen for connection changes
      if (connection && 'addEventListener' in connection) {
        connection.addEventListener('change', () => {
          this.connectionType = connection.effectiveType || 'unknown'
          this.saveData = connection.saveData || false
        })
      }
    }
  }

  shouldPreloadResource(resource: CriticalResource): boolean {
    // Don't preload on slow connections or save-data mode
    if (this.saveData || this.connectionType === 'slow-2g' || this.connectionType === '2g') {
      return resource.priority === 'critical'
    }

    // Preload critical and high priority resources on good connections
    if (this.connectionType === '4g' || this.connectionType === 'wifi') {
      return resource.priority === 'critical' || resource.priority === 'high'
    }

    // Conservative approach for 3g
    return resource.priority === 'critical'
  }

  getOptimalLoadStrategy(resource: CriticalResource): 'preload' | 'prefetch' | 'lazy' | 'defer' {
    if (!this.shouldPreloadResource(resource)) {
      return resource.condition === 'below-fold' ? 'lazy' : 'defer'
    }

    return resource.loadStrategy
  }
}

/**
 * Critical CSS extraction and inlining
 */
export class CriticalCSSExtractor {
  private criticalCSS: string = ''
  private nonCriticalCSS: string[] = []

  constructor() {
    this.extractCriticalCSS()
  }

  private extractCriticalCSS(): void {
    // Critical CSS for above-the-fold content
    this.criticalCSS = `
      /* Critical CSS - Above the fold */
      html { font-size-adjust: 0.5; }
      body { margin: 0; padding: 0; font-family: system-ui, -apple-system, sans-serif; }
      .font-loading { visibility: hidden; }
      .fonts-loaded .font-loading { visibility: visible; }
      
      /* Critical layout styles */
      .min-h-screen { min-height: 100vh; }
      .flex { display: flex; }
      .flex-col { flex-direction: column; }
      .flex-grow { flex-grow: 1; }
      
      /* Critical navigation styles */
      nav { position: sticky; top: 0; z-index: 50; }
      .h-16 { height: 4rem; }
      .bg-bg-main { background-color: hsl(var(--color-bg-main, #ffffff)); }
      
      /* Critical product page styles */
      .product-hero { display: grid; grid-template-columns: 1fr 1fr; gap: 2rem; }
      .product-image { aspect-ratio: 1; width: 100%; }
      .product-title { font-size: 2rem; font-weight: 700; margin-bottom: 1rem; }
      .product-price { font-size: 1.5rem; font-weight: 600; color: var(--primary, #000); }
      
      @media (max-width: 768px) {
        .product-hero { grid-template-columns: 1fr; }
        .product-title { font-size: 1.5rem; }
        .product-price { font-size: 1.25rem; }
      }
    `

    // Non-critical CSS that can be loaded later
    this.nonCriticalCSS = [
      //'/styles/components.css',
      '/styles/utilities.css',
      //'/styles/animations.css'
    ]
  }

  getCriticalCSS(): string {
    return this.criticalCSS
  }

  getNonCriticalCSS(): string[] {
    return this.nonCriticalCSS
  }

  inlineCriticalCSS(): string {
    return `<style id="critical-css">${this.criticalCSS}</style>`
  }
}

/**
 * Resource hints generator
 */
export class ResourceHintsGenerator {
  private connectionLoader: ConnectionAwareLoader
  private criticalResources: CriticalResource[]

  constructor() {
    this.connectionLoader = new ConnectionAwareLoader()
    this.criticalResources = this.defineCriticalResources()
  }

  private defineCriticalResources(): CriticalResource[] {
    return [
      // Critical fonts
      {
        type: 'font',
        url: '/fonts/AeonikPro-Regular.woff2',
        priority: 'critical',
        loadStrategy: 'preload',
        condition: 'above-fold'
      },
      {
        type: 'font',
        url: '/fonts/AeonikPro-Bold.woff2',
        priority: 'high',
        loadStrategy: 'preload',
        condition: 'above-fold'
      },
      
      // Critical images
      {
        type: 'image',
        url: '/Globe.avif',
        priority: 'high',
        loadStrategy: 'preload',
        condition: 'above-fold'
      },
      

      
      // Non-critical resources
      {
        type: 'css',
        url: '/styles/components.css',
        priority: 'medium',
        loadStrategy: 'prefetch',
        condition: 'below-fold'
      }
    ]
  }

  generateResourceHints(): ResourceHint[] {
    const hints: ResourceHint[] = []

    // DNS prefetch for external domains
    hints.push(
      { rel: 'dns-prefetch', href: '//fonts.gstatic.com' },
      { rel: 'dns-prefetch', href: '//cdn.jsdelivr.net' },
      { rel: 'preconnect', href: 'https://fonts.gstatic.com', crossOrigin: 'anonymous' }
    )

    // Generate hints based on critical resources and connection
    this.criticalResources.forEach(resource => {
      const strategy = this.connectionLoader.getOptimalLoadStrategy(resource)
      
      if (strategy === 'preload') {
        hints.push({
          rel: 'preload',
          href: resource.url,
          as: this.getResourceType(resource.type),
          type: this.getResourceMimeType(resource.type),
          crossOrigin: resource.type === 'font' ? 'anonymous' : undefined,
          priority: resource.priority === 'critical' ? 'high' : 'low'
        })
      } else if (strategy === 'prefetch') {
        hints.push({
          rel: 'prefetch',
          href: resource.url,
          as: this.getResourceType(resource.type)
        })
      }
    })

    return hints
  }

  private getResourceType(type: string): ResourceHint['as'] {
    switch (type) {
      case 'css': return 'style'
      case 'js': return 'script'
      case 'font': return 'font'
      case 'image': return 'image'
      default: return 'fetch'
    }
  }

  private getResourceMimeType(type: string): string | undefined {
    switch (type) {
      case 'css': return 'text/css'
      case 'js': return 'application/javascript'
      case 'font': return 'font/woff2'
      case 'image': return 'image/avif'
      default: return undefined
    }
  }
}

/**
 * Critical resource loading orchestrator
 */
export class CriticalResourceLoader {
  private hintsGenerator: ResourceHintsGenerator
  private cssExtractor: CriticalCSSExtractor
  private connectionLoader: ConnectionAwareLoader

  constructor() {
    this.hintsGenerator = new ResourceHintsGenerator()
    this.cssExtractor = new CriticalCSSExtractor()
    this.connectionLoader = new ConnectionAwareLoader()
  }

  getResourceHints(): ResourceHint[] {
    return this.hintsGenerator.generateResourceHints()
  }

  getCriticalCSS(): string {
    return this.cssExtractor.getCriticalCSS()
  }

  getNonCriticalCSS(): string[] {
    return this.cssExtractor.getNonCriticalCSS()
  }

  shouldLoadResource(resource: CriticalResource): boolean {
    return this.connectionLoader.shouldPreloadResource(resource)
  }

  /**
   * Load non-critical CSS asynchronously
   */
  loadNonCriticalCSS(): void {
    if (typeof window === 'undefined') return

    const nonCriticalCSS = this.getNonCriticalCSS()
    
    // Load non-critical CSS after page load
    window.addEventListener('load', () => {
      nonCriticalCSS.forEach(cssUrl => {
        const link = document.createElement('link')
        link.rel = 'stylesheet'
        link.href = cssUrl
        link.media = 'print'
        link.onload = () => {
          link.media = 'all'
        }
        document.head.appendChild(link)
      })
    })
  }

  /**
   * Preload critical resources based on connection
   */
  preloadCriticalResources(): void {
    if (typeof window === 'undefined') return

    const hints = this.getResourceHints()
    
    hints.forEach(hint => {
      if (hint.rel === 'preload') {
        const link = document.createElement('link')
        Object.assign(link, hint)
        document.head.appendChild(link)
      }
    })
  }
}

// Export singleton instance
export const criticalResourceLoader = new CriticalResourceLoader()